import { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { ConsentModal } from "@/components/ConsentModal";
import { ToastViewport } from "@/components/ToastViewport";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { useAccount } from "@/hooks/useAuth";
import { useSessionMe } from "@/hooks/useSessionMe";

import { Navbar } from "./Navbar";
import { RouteFallback } from "./RouteFallback";

/**
 * Routes accessibles **sans** consentement RGPD (S6-5, GAP-2).
 * - `/consentement-refuse` : page d'information après refus.
 * - `/politique-confidentialite` : page juridique publique RGPD art. 13
 *   (livrée S6-6, `frontend/src/pages/PrivacyPolicy.tsx`).
 */
const PUBLIC_PATHS = new Set<string>([
  "/consentement-refuse",
  "/politique-confidentialite",
  // S34-5 — boîte à idées : soumission possible connecté OU anonyme, sans gate
  // de consentement (ADR-016 D4). La modale RGPD ne doit pas bloquer la page.
  "/suggerer",
]);

export function AppLayout(): JSX.Element {
  const location = useLocation();
  const { data: me, isLoading: isSessionLoading } = useSessionMe();
  const { data: account, isLoading: isAccountLoading } = useAccount();

  // Porte de la modale RGPD **identity-aware** (fix obstruction art. 17).
  //
  // `consent_given_at` de `GET /sessions/me` ne concerne QUE la session
  // anonyme (cookie `sunmatch_anon`). Un compte, lui, a TOUJOURS consenti au
  // signup (`Account.consent_given_at` NOT NULL, garanti serveur). Sans ce
  // garde, un utilisateur connecté dont la session anon du navigateur n'a
  // jamais consenti se voyait imposer la modale ; un clic « Refuser » le
  // renvoyait sur `/consentement-refuse` et rendait `/settings` inatteignable
  // — donc l'empêchait de supprimer son compte (droit à l'effacement bloqué).
  // Règle : dès qu'un compte est présent, la modale anon ne bloque JAMAIS.
  //
  // Anti-flash (S27 « flash du toggle ») : on n'affiche rien tant que l'une des
  // deux identités (session anon / compte) est encore inconnue — sinon la
  // modale flasherait pendant le fetch de `/auth/me` pour un compte connecté.
  const identityResolved = !isSessionLoading && !isAccountLoading;

  const shouldShowModal =
    identityResolved &&
    !account &&
    !!me &&
    me.consent_given_at === null &&
    !PUBLIC_PATHS.has(location.pathname);

  return (
    // S16-1 — `overflow-x-hidden` : filet de sécurité contre tout
    // débordement horizontal résiduel (la cause structurelle reste corrigée
    // à la source, ce garde-fou ne fait que masquer un dépassement marginal).
    // 2026-06-13 — fond dégradé ambre (même teinte que le hero de la landing,
    // `from-amber-50 to-white`) déployé sur tout l'app shell + footer landing
    // partagé en bas de page (`flex-col` + `flex-1` colle le footer au pied
    // même quand le contenu est court).
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-gradient-to-b from-amber-50 to-white text-slate-900">
      <Navbar />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        {/* Frontière Suspense des routes lazy (S30 A5) : le shell (Navbar +
            footer) reste affiché, seul le contenu montre le fallback. */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <LandingFooter />
      {shouldShowModal ? <ConsentModal /> : null}
      <ToastViewport />
    </div>
  );
}
